import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
  eachDayOfInterval,
} from 'date-fns';
import { vi } from 'date-fns/locale';
import {
  Calendar,
  Download,
  TrendingUp,
  DollarSign,
  Users,
  BarChart3,
} from 'lucide-react';
import { QuickDateRange } from '../components/common/QuickDateRange';
import { DateRangePreset, getDateRangeForPreset } from '../utils/dateRangeUtils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { shiftService } from '../services/shiftService';
import { staffProfileService } from '../services/staffProfileService';
import { attendanceService } from '../services/attendanceService';
import { usersService } from '../services/usersService';
import bonusService, { Bonus } from '../services/bonusService';
import { useTranslation } from '../hooks/useTranslation';
import { getFiscalMonthRange } from '../stores/fiscalMonthSettingsStore';
import { StaffProfile } from 'public-contracts';
import { Shift } from '../services/shiftService';

// Modern UI Components (reused from OrdersReport)
const Card = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-card text-card-foreground rounded-lg border shadow-sm ${className}`}
  >
    {children}
  </div>
);

const CardHeader = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
);

const CardTitle = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h3
    className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
  >
    {children}
  </h3>
);

const CardDescription = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p className={`text-sm text-muted-foreground ${className}`}>{children}</p>
);

const CardContent = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`p-6 pt-0 ${className}`}>{children}</div>;

const Button = ({
  children,
  onClick,
  disabled = false,
  variant = 'default',
  className = '',
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
  className?: string;
  [key: string]: any;
}) => {
  const baseClasses =
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline:
      'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({
  type = 'text',
  value,
  onChange,
  placeholder,
  className = '',
  ...props
}: {
  type?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  [key: string]: any;
}) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Label = ({
  children,
  className = '',
  htmlFor,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  htmlFor?: string;
  [key: string]: any;
}) => (
  <label
    htmlFor={htmlFor}
    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
    {...props}
  >
    {children}
  </label>
);

const Badge = ({
  children,
  variant = 'default',
  className = '',
}: {
  children: React.ReactNode;
  variant?: 'default' | 'secondary' | 'outline';
  className?: string;
}) => {
  const baseClasses =
    'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

  const variantClasses = {
    default:
      'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
    secondary:
      'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'text-foreground',
  };

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {children}
    </div>
  );
};


const Select = ({
  value,
  onChange,
  className = '',
  children,
  ...props
}: {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  children: React.ReactNode;
  [key: string]: any;
}) => (
  <select
    value={value}
    onChange={onChange}
    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </select>
);

// Extended StaffProfile interface to include employment type and vacation info
interface ExtendedStaffProfile extends StaffProfile {
  employmentType?: 'full-time' | 'part-time' | 'contract';
  vacationDaysPerYear?: number;
  vacationDaysUsed?: number;
  flexibilityDays?: number[]; // Days of week (0=Sunday, 1=Monday, etc.) they can work
  dayOffs?: string[]; // Specific dates they're off (YYYY-MM-DD format)
  baseSalary?: number; // For full-time employees with guaranteed salary
  guaranteedHoursPerWeek?: number; // For full-time employees
  metadata?: Record<string, any>;
}

interface BonusDetail {
  bonusName: string;
  amount: number;
  currency: string;
}

interface SalaryForecastData {
  staffId: string;
  staffName: string;
  totalHours: number;
  hourlyRate: number;
  currency: string;
  forecastedSalary: number;
  shiftCount: number;
  employmentType?: 'full-time' | 'part-time' | 'contract';
  vacationDaysInPeriod: number;
  absencesOutsideVacation: number;
  paidVacationSalary: number;
  baseSalaryAmount?: number;
  totalBonuses: number;
  bonusDetails: BonusDetail[];
}

interface DailySalaryData {
  date: string;
  totalSalary: number;
  totalHours: number;
  staffCount: number;
  fullTimeCount: number;
  partTimeCount: number;
}

const ModernSalaryForecastReport = () => {
  const { translate, locale } = useTranslation();
  const [selectedPreset, setSelectedPreset] =
    useState<DateRangePreset>('thisWeek');
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    return {
      from: format(weekStart, 'yyyy-MM-dd'),
      to: format(weekEnd, 'yyyy-MM-dd'),
    };
  });
  const [data, setData] = useState<{
    staffForecasts: SalaryForecastData[];
    dailyTotals: DailySalaryData[];
    periodTotal: number;
    periodTotalHours: number;
    totalStaff: number;
    fullTimeStaff: number;
    partTimeStaff: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const isAdjustingDateRange = useRef(false);

  const handleFetchReport = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);

      // Fetch shifts, staff profiles, users, attendance records, and bonuses in parallel
      const [shifts, staffProfiles, allUsers, allAttendanceRecords, allBonuses] = await Promise.all([
        shiftService.getShiftsByDateRange(fromDate, toDate),
        staffProfileService.getStaffProfiles({ page: 1, size: 1000, excludeArchivedUsers: true }), // Fetch all profiles, excluding archived users
        usersService.getUsers({ size: 1000 }).catch(() => []), // Fetch all users to get names
        attendanceService.getAttendanceRecords().catch(() => []), // Fetch attendance records, ignore errors
        bonusService.getBonuses({ size: 1000 }).catch(() => []), // Fetch all active bonuses
      ]);

      // Filter to only active bonuses
      const activeBonuses = (allBonuses as Bonus[]).filter(bonus => bonus.isActive);

      // Create a map of userId to user name
      const userNameMap = new Map<string, string>();
      allUsers.forEach((user) => {
        if (user.id) {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id;
          userNameMap.set(user.id, fullName);
        }
      });

      // Create extended staff profile map with employment info
      const staffProfileMap = new Map<string, ExtendedStaffProfile>();
      const staffRateMap = new Map<string, { rate: number; currency: string }>();
      
      staffProfiles.forEach((profile) => {
        if (profile.userId) {
          // Extend profile with metadata or default values
          const extendedProfile: ExtendedStaffProfile = {
            ...profile,
            employmentType: (profile as any).employmentType || (profile as any).metadata?.employmentType || 'part-time',
            vacationDaysPerYear: (profile as any).vacationDaysPerYear || (profile as any).metadata?.vacationDaysPerYear || 0,
            vacationDaysUsed: (profile as any).vacationDaysUsed || (profile as any).metadata?.vacationDaysUsed || 0,
            flexibilityDays: (profile as any).flexibilityDays || (profile as any).metadata?.flexibilityDays || undefined,
            dayOffs: (profile as any).dayOffs || (profile as any).metadata?.dayOffs || [],
            baseSalary: (profile as any).baseSalary || (profile as any).metadata?.baseSalary || undefined,
            guaranteedHoursPerWeek: (profile as any).guaranteedHoursPerWeek || (profile as any).metadata?.guaranteedHoursPerWeek || undefined,
            metadata: (profile as any).metadata || {},
          };
          
          staffProfileMap.set(profile.userId, extendedProfile);
          staffRateMap.set(profile.userId, {
            rate: profile.ratePerHour || 0,
            currency: profile.ratePerHourCurrency || 'VND',
          });
        }
      });

      // Calculate period duration in days
      const periodDays = eachDayOfInterval({ start: fromDate, end: toDate });
      const periodDaysCount = periodDays.length;
      
      // Calculate vacation days per period (proportional to year)
      const vacationDaysPerPeriod = (vacationDaysPerYear: number, periodDays: number) => {
        return (vacationDaysPerYear / 365) * periodDays;
      };

      // Filter shifts to only include scheduled/confirmed shifts (not cancelled)
      const activeShifts = shifts.filter(
        (shift) =>
          shift.status === 'scheduled' || shift.status === 'confirmed'
      );

      // Calculate salary forecast for each staff member
      const staffForecastMap = new Map<string, SalaryForecastData>();

      // Initialize forecasts for all staff members (including those without shifts)
      staffProfileMap.forEach((profile, userId) => {
        const staffRate = staffRateMap.get(userId) || {
          rate: 0,
          currency: 'VND',
        };
        
        staffForecastMap.set(userId, {
          staffId: userId,
          staffName: userNameMap.get(userId) || 'Unknown', // Use user name from users list
          totalHours: 0,
          hourlyRate: staffRate.rate,
          currency: staffRate.currency,
          forecastedSalary: 0,
          shiftCount: 0,
          employmentType: profile.employmentType,
          vacationDaysInPeriod: 0,
          absencesOutsideVacation: 0,
          paidVacationSalary: 0,
          baseSalaryAmount: profile.baseSalary,
          totalBonuses: 0,
          bonusDetails: [],
        });
      });

      // Process shifts to calculate hours and salary
      activeShifts.forEach((shift) => {
        if (!shift.staffId) return;

        const startTime = new Date(shift.startTime);
        const endTime = new Date(shift.endTime);
        const hours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

        const forecast = staffForecastMap.get(shift.staffId);
        if (forecast) {
          // Prefer user name from users list, fallback to shift staffName
          forecast.staffName = userNameMap.get(shift.staffId) || shift.staffName || forecast.staffName;
          forecast.totalHours += hours;
          forecast.shiftCount += 1;
        }
      });

      // Process full-time employees and calculate vacation/absence adjustments
      staffProfileMap.forEach((profile, userId) => {
        const forecast = staffForecastMap.get(userId);
        if (!forecast) return;

        const isFullTime = profile.employmentType === 'full-time';
        
        if (isFullTime) {
          // Calculate vacation days available for this period
          const vacationDaysAvailable = Math.max(0, 
            (profile.vacationDaysPerYear || 0) - (profile.vacationDaysUsed || 0)
          );
          const vacationDaysForPeriod = Math.min(
            vacationDaysPerPeriod(vacationDaysAvailable, periodDaysCount),
            periodDaysCount
          );
          
          forecast.vacationDaysInPeriod = Math.floor(vacationDaysForPeriod);
          
          // Calculate paid vacation salary (assuming 8 hours per day)
          const vacationHours = forecast.vacationDaysInPeriod * 8;
          forecast.paidVacationSalary = vacationHours * forecast.hourlyRate;
          
          // Check for absences outside vacation days
          // Get attendance records for this staff member in the period
          const staffAttendanceRecords = allAttendanceRecords.filter(
            (record) => record.userId === userId &&
              new Date(record.date) >= fromDate &&
              new Date(record.date) <= toDate
          );
          
          // Count days with scheduled shifts but no attendance (absences)
          const shiftDates = new Set(
            activeShifts
              .filter(s => s.staffId === userId)
              .map(s => format(new Date(s.startTime), 'yyyy-MM-dd'))
          );
          
          const attendanceDates = new Set(
            staffAttendanceRecords
              .filter(r => r.isIn)
              .map(r => format(new Date(r.date), 'yyyy-MM-dd'))
          );
          
          // Days with shifts but no attendance (potential absences)
          // For future shifts, we assume attendance for forecast purposes
          const nowStr = format(new Date(), 'yyyy-MM-dd');
          const absentDays = Array.from(shiftDates).filter(
            date => date < nowStr && !attendanceDates.has(date)
          );
          
          // Subtract vacation days from absent days
          const absencesOutsideVacation = Math.max(0, 
            absentDays.length - forecast.vacationDaysInPeriod
          );
          
          forecast.absencesOutsideVacation = absencesOutsideVacation;
          
          // For full-time with base salary, use base salary if available
          if (profile.baseSalary) {
            // Calculate proportional base salary for the period
            // If it's exactly a calendar month, use the baseSalary directly to ensure accuracy (e.g. exactly 6M)
            const isFullCalendarMonth = 
              format(fromDate, 'yyyy-MM-dd') === format(startOfMonth(fromDate), 'yyyy-MM-dd') &&
              format(toDate, 'yyyy-MM-dd') === format(endOfMonth(fromDate), 'yyyy-MM-dd');
            
            let baseSalaryForPeriod = 0;
            if (isFullCalendarMonth) {
              baseSalaryForPeriod = (profile.baseSalary || 0);
            } else {
              // Proportional calculation using average days in a month (365.25 / 12)
              const averageDaysInMonth = 30.4375;
              baseSalaryForPeriod = ((profile.baseSalary || 0) / averageDaysInMonth) * periodDaysCount;
            }
            
            // For full-timers, base salary covers typical work days including paid leave.
            // We don't add paidVacationSalary as it's already part of the monthly rate.
            forecast.forecastedSalary = baseSalaryForPeriod;
            
            // Subtract unpaid absences (assuming 8 hours per day)
            const unpaidAbsenceHours = forecast.absencesOutsideVacation * 8;
            const unpaidAbsenceDeduction = unpaidAbsenceHours * forecast.hourlyRate;
            forecast.forecastedSalary = Math.max(0, forecast.forecastedSalary - unpaidAbsenceDeduction);
          } else {
            // Calculate based on shifts + vacation - unpaid absences
            forecast.forecastedSalary = (forecast.totalHours * forecast.hourlyRate) + forecast.paidVacationSalary;
            
            // Subtract unpaid absences (since vacation pay was added, we subtract full absence cost)
            const unpaidAbsenceHours = forecast.absencesOutsideVacation * 8;
            const unpaidAbsenceDeduction = unpaidAbsenceHours * forecast.hourlyRate;
            forecast.forecastedSalary = Math.max(0, forecast.forecastedSalary - unpaidAbsenceDeduction);
          }
          
          // Handle guaranteed hours for full-time employees
          if (profile.guaranteedHoursPerWeek && !profile.baseSalary) {
            const periodWeeks = periodDaysCount / 7;
            const guaranteedHoursInPeriod = profile.guaranteedHoursPerWeek * periodWeeks;
            const guaranteedSalary = guaranteedHoursInPeriod * forecast.hourlyRate;
            
            // Use the higher of actual hours or guaranteed hours
            if (forecast.totalHours < guaranteedHoursInPeriod) {
              // We assume guaranteed hours cover for low-shift periods but not necessarily vacation on top
              forecast.forecastedSalary = guaranteedSalary;
              // Still subtract unpaid absences
              const unpaidAbsenceHours = forecast.absencesOutsideVacation * 8;
              const unpaidAbsenceDeduction = unpaidAbsenceHours * forecast.hourlyRate;
              forecast.forecastedSalary = Math.max(0, forecast.forecastedSalary - unpaidAbsenceDeduction);
            }
          }
        } else {
          // Part-time employees: calculate based on shifts only
          forecast.forecastedSalary = forecast.totalHours * forecast.hourlyRate;
        }
      });

      // Calculate bonuses for each staff member
      const calculateBonuses = (
        forecast: SalaryForecastData,
        profile: ExtendedStaffProfile,
        staffShifts: Shift[]
      ) => {
        let totalBonuses = 0;
        const bonusDetails: Array<{ bonusName: string; amount: number; currency: string }> = [];
        const bonusCountMap = new Map<string, number>(); // Track how many times each bonus applies

        activeBonuses.forEach((bonus) => {
          // Check if bonus applies to this staff member
          let appliesToStaff = false;
          let matchCount = 0;

          // Check staffing type match
          if (bonus.staffingType) {
            if (bonus.staffingType !== profile.employmentType) {
              return; // Skip if staffing type doesn't match
            }
            appliesToStaff = true;
          }

          // Check position match (if bonus has position requirement)
          if (bonus.position) {
            // We'd need position info from staff profile - for now, skip position check
            // In a real implementation, you'd check profile.positionId or similar
          }

          // Process shifts to check bonus conditions
          staffShifts.forEach((shift) => {
            const shiftStart = new Date(shift.startTime);
            const shiftDayOfWeek = shiftStart.getDay();
            const shiftHour = shiftStart.getHours();

            // Check entity type - only process shift bonuses for shifts
            if (bonus.entityType !== 'shift' && bonus.entityType !== 'other') {
              return; // Skip this shift if bonus doesn't apply to shifts
            }

            let shiftMatches = true;

            // Check day of week
            if (bonus.dayOfWeek && bonus.dayOfWeek.length > 0) {
              if (!bonus.dayOfWeek.includes(shiftDayOfWeek)) {
                shiftMatches = false; // Shift doesn't match day requirement
              }
            }

            // Check time of day
            if (shiftMatches && bonus.timeOfDay) {
              if (bonus.timeOfDay === 'custom' && bonus.customTimeRange) {
                const startHour = parseInt(bonus.customTimeRange.startTime.split(':')[0]);
                const endHour = parseInt(bonus.customTimeRange.endTime.split(':')[0]);
                if (shiftHour < startHour || shiftHour >= endHour) {
                  shiftMatches = false; // Shift doesn't match custom time range
                }
              } else {
                // Check predefined time ranges
                const timeRanges: Record<string, [number, number]> = {
                  morning: [6, 12],
                  afternoon: [12, 18],
                  evening: [18, 22],
                  night: [22, 6],
                };
                const [startHour, endHour] = timeRanges[bonus.timeOfDay] || [0, 24];
                if (bonus.timeOfDay === 'night') {
                  // Night shift spans midnight (22:00 to 06:00)
                  if (shiftHour < startHour && shiftHour >= endHour) {
                    shiftMatches = false; // Shift doesn't match night range
                  }
                } else {
                  if (shiftHour < startHour || shiftHour >= endHour) {
                    shiftMatches = false; // Shift doesn't match time range
                  }
                }
              }
            }

            // If shift matches all conditions, count it
            if (shiftMatches) {
              matchCount++;
              appliesToStaff = true;
            }
          });

          // If bonus applies, add it
          if (appliesToStaff && matchCount > 0) {
            // Convert bonus amount to forecast currency if needed
            let bonusAmount = bonus.bonusAmount;
            if (bonus.bonusCurrency !== forecast.currency) {
              // Simple conversion - in production, use actual exchange rates
              // For now, assume 1:1 if same currency family, or use a conversion factor
              // This is a placeholder - implement proper currency conversion
              console.warn(`Currency conversion needed: ${bonus.bonusCurrency} to ${forecast.currency}`);
            }

            const totalBonusForStaff = bonusAmount * matchCount;
            totalBonuses += totalBonusForStaff;
            
            if (!bonusCountMap.has(bonus.id)) {
              bonusDetails.push({
                bonusName: bonus.name,
                amount: totalBonusForStaff,
                currency: forecast.currency,
              });
              bonusCountMap.set(bonus.id, matchCount);
            } else {
              // Update existing bonus detail
              const existingIndex = bonusDetails.findIndex(
                (d) => d.bonusName === bonus.name
              );
              if (existingIndex >= 0) {
                bonusDetails[existingIndex].amount = totalBonusForStaff;
              }
            }
          }
        });

        forecast.totalBonuses = totalBonuses;
        forecast.bonusDetails = bonusDetails;
        forecast.forecastedSalary += totalBonuses;
      };

      // Apply bonuses to each staff member
      staffProfileMap.forEach((profile, userId) => {
        const forecast = staffForecastMap.get(userId);
        if (!forecast) return;

        const staffShifts = activeShifts.filter((s) => s.staffId === userId);
        calculateBonuses(forecast, profile, staffShifts);
      });

      // Filter out staff with no shifts and no base salary (unless they're full-time with vacation)
      const staffForecasts = Array.from(staffForecastMap.values()).filter(
        (forecast) => 
          forecast.shiftCount > 0 || 
          forecast.baseSalaryAmount || 
          (forecast.employmentType === 'full-time' && forecast.vacationDaysInPeriod > 0)
      );

      // Calculate daily totals
      const dailyTotalsMap = new Map<string, DailySalaryData>();

      activeShifts.forEach((shift) => {
        if (!shift.staffId) return;

        const shiftDate = format(new Date(shift.startTime), 'yyyy-MM-dd');
        const startTime = new Date(shift.startTime);
        const endTime = new Date(shift.endTime);
        const hours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

        const staffRate = staffRateMap.get(shift.staffId) || {
          rate: 0,
          currency: 'VND',
        };
        const salary = hours * staffRate.rate;

        if (!dailyTotalsMap.has(shiftDate)) {
          dailyTotalsMap.set(shiftDate, {
            date: shiftDate,
            totalSalary: 0,
            totalHours: 0,
            staffCount: 0,
            fullTimeCount: 0,
            partTimeCount: 0,
          });
        }

        const dailyData = dailyTotalsMap.get(shiftDate)!;
        dailyData.totalSalary += salary;
        dailyData.totalHours += hours;
      });

      // Count unique staff per day
      const dailyStaffCountMap = new Map<string, Set<string>>();
      const dailyFullTimeCountMap = new Map<string, Set<string>>();
      const dailyPartTimeCountMap = new Map<string, Set<string>>();

      activeShifts.forEach((shift) => {
        if (!shift.staffId) return;
        const shiftDate = format(new Date(shift.startTime), 'yyyy-MM-dd');
        const profile = staffProfileMap.get(shift.staffId);
        const type = profile?.employmentType || 'part-time';

        if (!dailyStaffCountMap.has(shiftDate)) {
          dailyStaffCountMap.set(shiftDate, new Set());
          dailyFullTimeCountMap.set(shiftDate, new Set());
          dailyPartTimeCountMap.set(shiftDate, new Set());
        }
        
        dailyStaffCountMap.get(shiftDate)!.add(shift.staffId);
        if (type === 'full-time') {
          dailyFullTimeCountMap.get(shiftDate)!.add(shift.staffId);
        } else {
          dailyPartTimeCountMap.get(shiftDate)!.add(shift.staffId);
        }
      });

      dailyTotalsMap.forEach((dailyData, date) => {
        dailyData.staffCount = dailyStaffCountMap.get(date)?.size || 0;
        dailyData.fullTimeCount = dailyFullTimeCountMap.get(date)?.size || 0;
        dailyData.partTimeCount = dailyPartTimeCountMap.get(date)?.size || 0;
      });

      const dailyTotals = Array.from(dailyTotalsMap.values()).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      const periodTotal = staffForecasts.reduce(
        (sum, f) => sum + f.forecastedSalary,
        0
      );
      const periodTotalHours = staffForecasts.reduce(
        (sum, f) => sum + f.totalHours,
        0
      );

      setData({
        staffForecasts,
        dailyTotals,
        periodTotal,
        periodTotalHours,
        totalStaff: staffForecasts.length,
        fullTimeStaff: staffForecasts.filter(f => f.employmentType === 'full-time').length,
        partTimeStaff: staffForecasts.filter(f => f.employmentType === 'part-time').length,
      });
    } catch (error) {
      setIsError(true);
      console.error('Error fetching salary forecast:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPreset('custom');
    setDateRange({
      ...dateRange,
      [e.target.name]: e.target.value,
    });
  };

  const handleRangeChange = (from: string, to: string, preset: DateRangePreset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setDateRange({ from, to });
    }
  };

  // Auto-fetch data when component mounts or date range changes
  useEffect(() => {
    if (dateRange.from && dateRange.to && !isAdjustingDateRange.current) {
      handleFetchReport();
    }
    if (isAdjustingDateRange.current) {
      setTimeout(() => {
        isAdjustingDateRange.current = false;
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.from, dateRange.to]);

  const formatCurrency = (value: number, currency: string = 'VND') => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: currency === 'VND' ? 'VND' : currency,
    }).format(value);
  };

  // Calculate the last date with actual data
  const lastDateWithData = useMemo(() => {
    if (!data?.dailyTotals) return null;

    const datesWithData = data.dailyTotals.filter(
      (daily) => daily.totalSalary > 0 || daily.totalHours > 0
    );

    if (datesWithData.length === 0) return null;

    const sortedDates = datesWithData
      .map((d) => d.date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    return sortedDates[sortedDates.length - 1];
  }, [data]);


  // Prepare chart data - only include dates with actual data
  const chartData = useMemo(() => {
    if (!data?.dailyTotals) return [];

    return data.dailyTotals
      .filter((daily) => {
        const hasSalary = daily.totalSalary > 0;
        const hasHours = daily.totalHours > 0;
        if (lastDateWithData && daily.date > lastDateWithData) {
          return false;
        }
        return hasSalary || hasHours;
      })
      .map((daily) => ({
        date: format(new Date(daily.date), 'MMM d'),
        fullDate: daily.date,
        salary: daily.totalSalary,
        hours: daily.totalHours,
        staffCount: daily.staffCount,
        fullTimeCount: daily.fullTimeCount,
        partTimeCount: daily.partTimeCount,
      }));
  }, [data, lastDateWithData]);


  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {translate(
              'pos.salary_forecast.title',
              'Salary Forecast Report'
            )}
          </h1>
          <p className="text-muted-foreground">
            {translate(
              'pos.salary_forecast.subtitle',
              'Forecast staff salaries based on scheduled shifts'
            )}
          </p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          {translate(
            'pos.salary_forecast.export_report',
            'Export Report'
          )}
        </Button>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {translate(
              'pos.salary_forecast.select_date_range',
              'Select Date Range'
            )}
          </CardTitle>
          <CardDescription>
            {translate(
              'pos.salary_forecast.choose_period',
              'Choose a time period to forecast salaries'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <QuickDateRange
              selectedPreset={selectedPreset}
              onRangeChange={handleRangeChange}
            />

            {/* Date Range Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="from">
                  {translate('pos.orders_report.from_date', 'From Date')}
                </Label>
                <Input
                  id="from"
                  name="from"
                  type="date"
                  value={dateRange.from}
                  onChange={handleDateChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="to">
                  {translate('pos.orders_report.to_date', 'To Date')}
                </Label>
                <Input
                  id="to"
                  name="to"
                  type="date"
                  value={dateRange.to}
                  onChange={handleDateChange}
                />
              </div>
              <Button
                onClick={handleFetchReport}
                disabled={isLoading}
                className="h-10 gap-2"
              >
                {isLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <TrendingUp className="h-4 w-4" />
                )}
                {isLoading
                  ? 'Loading...'
                  : translate(
                      'pos.salary_forecast.generate_report',
                      'Generate Report'
                    )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <TrendingUp className="h-6 w-6 text-primary animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {translate(
                    'pos.salary_forecast.loading_report',
                    'Loading Salary Forecast'
                  )}
                </h3>
                <p className="text-muted-foreground">
                  {translate(
                    'pos.salary_forecast.fetching_data',
                    'Fetching shifts and staff data...'
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {isError && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <TrendingUp className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {translate(
                    'pos.salary_forecast.failed_to_load',
                    'Failed to Load Report'
                  )}
                </h3>
                <p className="text-muted-foreground">
                  {translate(
                    'pos.salary_forecast.try_again_message',
                    'Please try again or check your connection'
                  )}
                </p>
              </div>
              <Button onClick={handleFetchReport} variant="outline">
                {translate('pos.orders_report.try_again', 'Try Again')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Results */}
      {data && !isLoading && !isError && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate(
                      'pos.salary_forecast.total_forecasted',
                      'Total Forecasted Salary'
                    )}
                  </span>
                </div>
                <div className="text-2xl font-bold">
                  <span data-sensitive-field="amount">
                    {data.staffForecasts.length > 0
                      ? formatCurrency(
                          data.periodTotal,
                          data.staffForecasts[0].currency
                        )
                      : formatCurrency(0)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  <p>
                    {data.totalStaff}{' '}
                    {translate('pos.salary_forecast.staff_members', {
                      count: data.totalStaff,
                    })}
                  </p>
                  {data.totalStaff > 0 && (
                    <div className="pl-2 border-l-2 border-muted">
                      {data.fullTimeStaff > 0 && (
                        <p>
                          {data.fullTimeStaff}{' '}
                          {translate('pos.staff.profile.full_time', 'Full-Time')}
                        </p>
                      )}
                      {data.partTimeStaff > 0 && (
                        <p>
                          {data.partTimeStaff}{' '}
                          {translate('pos.salary_forecast.shift_based', 'Shift-Based')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate(
                      'pos.salary_forecast.total_hours',
                      'Total Hours'
                    )}
                  </span>
                </div>
                <div className="text-2xl font-bold">
                  {data.periodTotalHours.toFixed(1)}h
                </div>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.salary_forecast.scheduled_hours',
                    'Scheduled hours in period'
                  )}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {translate(
                      'pos.salary_forecast.average_daily',
                      'Average Daily Salary'
                    )}
                  </span>
                </div>
                <div className="text-2xl font-bold">
                  <span data-sensitive-field="amount">
                    {chartData.length > 0
                      ? formatCurrency(
                          data.periodTotal / chartData.length,
                          data.staffForecasts[0]?.currency || 'VND'
                        )
                      : formatCurrency(0)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.salary_forecast.per_day_average',
                    'Per day average'
                  )}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Salary Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  {translate(
                    'pos.salary_forecast.salary_chart',
                    'Salary Forecast Chart'
                  )}
                </CardTitle>
                <CardDescription>
                  {translate(
                    'pos.salary_forecast.chart_description',
                    'Daily forecasted salary breakdown'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="w-full h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        yAxisId="salary"
                        orientation="left"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          if (value >= 1000000) {
                            return `${(value / 1000000).toFixed(1)}M`;
                          }
                          if (value >= 1000) {
                            return `${(value / 1000).toFixed(1)}K`;
                          }
                          return value.toString();
                        }}
                      />
                      <YAxis
                        yAxisId="hours"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const dataPoint = payload[0].payload;
                            const dateLocale = locale === 'vi' ? vi : undefined;
                            const formattedDate = format(
                              new Date(dataPoint.fullDate),
                              'EEEE, MMMM d, yyyy',
                              dateLocale ? { locale: dateLocale } : undefined
                            );

                            return (
                              <div className="bg-background border rounded-lg shadow-lg p-4 space-y-2">
                                <p className="font-bold text-sm border-b pb-2 mb-2">
                                  {formattedDate}
                                </p>
                                <div className="space-y-1">
                                  <div className="flex justify-between gap-8">
                                    <span className="text-sm text-muted-foreground">
                                      {translate('pos.salary_forecast.salary', 'Salary')}
                                    </span>
                                    <span className="text-sm font-bold text-blue-600">
                                      {formatCurrency(
                                        dataPoint.salary,
                                        data?.staffForecasts[0]?.currency || 'VND'
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-8">
                                    <span className="text-sm text-muted-foreground">
                                      {translate('pos.salary_forecast.hours', 'Hours')}
                                    </span>
                                    <span className="text-sm font-bold text-emerald-600">
                                      {dataPoint.hours.toFixed(1)}h
                                    </span>
                                  </div>
                                  <div className="pt-2 border-t mt-2">
                                    <div className="flex justify-between gap-8">
                                      <span className="text-sm text-muted-foreground">
                                        {translate('pos.salary_forecast.staff', { count: dataPoint.staffCount })}
                                      </span>
                                      <span className="text-sm font-bold">
                                        {dataPoint.staffCount}
                                      </span>
                                    </div>
                                    <div className="pl-3 mt-1 space-y-0.5 border-l-2">
                                      {dataPoint.fullTimeCount > 0 && (
                                        <div className="flex justify-between gap-8 opacity-80">
                                          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                            {translate('pos.salary_forecast.full_time', 'Full-Time')}
                                          </span>
                                          <span className="text-xs font-medium">
                                            {dataPoint.fullTimeCount}
                                          </span>
                                        </div>
                                      )}
                                      {dataPoint.partTimeCount > 0 && (
                                        <div className="flex justify-between gap-8 opacity-80">
                                          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                            {translate('pos.salary_forecast.shift_based', 'Shift-Based')}
                                          </span>
                                          <span className="text-xs font-medium">
                                            {dataPoint.partTimeCount}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend
                        formatter={(value) => {
                          if (value === 'salary') {
                            return translate(
                              'pos.salary_forecast.salary',
                              'Salary'
                            );
                          }
                          if (value === 'hours') {
                            return translate(
                              'pos.salary_forecast.hours',
                              'Hours'
                            );
                          }
                          return value;
                        }}
                      />
                      <Line
                        yAxisId="salary"
                        type="monotone"
                        dataKey="salary"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name="salary"
                      />
                      <Line
                        yAxisId="hours"
                        type="monotone"
                        dataKey="hours"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name="hours"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Staff Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate(
                  'pos.salary_forecast.staff_breakdown',
                  'Staff Salary Breakdown'
                )}
              </CardTitle>
              <CardDescription>
                {translate(
                  'pos.salary_forecast.staff_breakdown_description',
                  'Forecasted salary by staff member'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.staffForecasts.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">
                    {translate(
                      'pos.salary_forecast.no_shifts_found',
                      'No Shifts Found'
                    )}
                  </h3>
                  <p className="text-muted-foreground">
                    {translate(
                      'pos.salary_forecast.no_shifts_message',
                      'No scheduled shifts found for the selected period'
                    )}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.staffForecasts
                    .sort((a, b) => b.forecastedSalary - a.forecastedSalary)
                    .map((forecast) => (
                      <div
                        key={forecast.staffId}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card"
                      >
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="flex flex-col flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{forecast.staffName}</span>
                              {forecast.employmentType === 'full-time' && (
                                <Badge variant="outline" className="text-xs">
                                  {translate(
                                    'pos.salary_forecast.full_time',
                                    'Full-Time'
                                  )}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-4 mt-1">
                              <span className="text-sm text-muted-foreground">
                                {forecast.shiftCount}{' '}
                                {translate(
                                  'pos.salary_forecast.shifts',
                                  {
                                    count: forecast.shiftCount,
                                  }
                                )}
                              </span>
                              {forecast.vacationDaysInPeriod > 0 && (
                                <span className="text-sm text-green-600">
                                  {forecast.vacationDaysInPeriod}{' '}
                                  {translate(
                                    'pos.salary_forecast.vacation_days',
                                    'vacation days'
                                  )}
                                </span>
                              )}
                              {forecast.totalBonuses > 0 && (
                                <span className="text-sm text-blue-600">
                                  {formatCurrency(forecast.totalBonuses, forecast.currency)}{' '}
                                  {translate(
                                    'pos.salary_forecast.bonuses',
                                    'bonuses'
                                  )}
                                </span>
                              )}
                              {forecast.absencesOutsideVacation > 0 && (
                                <span className="text-sm text-red-600">
                                  {forecast.absencesOutsideVacation}{' '}
                                  {translate(
                                    'pos.salary_forecast.unpaid_absences',
                                    'unpaid absences'
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col items-end">
                            <Badge variant="secondary" className="mb-1">
                              <span data-sensitive-field="amount">
                                {formatCurrency(
                                  forecast.forecastedSalary,
                                  forecast.currency
                                )}
                              </span>
                            </Badge>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs text-muted-foreground">
                                {forecast.totalHours.toFixed(1)}h @{' '}
                                {formatCurrency(
                                  forecast.hourlyRate,
                                  forecast.currency
                                )}
                                /h
                              </span>
                              {forecast.paidVacationSalary > 0 && (
                                <span className="text-xs text-green-600">
                                  +{formatCurrency(
                                    forecast.paidVacationSalary,
                                    forecast.currency
                                  )}{' '}
                                  {translate(
                                    'pos.salary_forecast.vacation_pay',
                                    'vacation pay'
                                  )}
                                </span>
                              )}
                              {forecast.baseSalaryAmount && (
                                <span className="text-xs text-blue-600">
                                  {translate(
                                    'pos.salary_forecast.base_salary',
                                    'Base salary included'
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate(
                  'pos.salary_forecast.daily_breakdown',
                  'Daily Breakdown'
                )}
              </CardTitle>
              <CardDescription>
                {translate(
                  'pos.salary_forecast.daily_breakdown_description',
                  'Daily salary forecast breakdown'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.dailyTotals.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">
                    {translate(
                      'pos.salary_forecast.no_data_found',
                      'No Data Found'
                    )}
                  </h3>
                  <p className="text-muted-foreground">
                    {translate(
                      'pos.salary_forecast.no_data_message',
                      'No salary data found for the selected period'
                    )}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.dailyTotals
                    .filter((daily) => {
                      const hasSalary = daily.totalSalary > 0;
                      const hasHours = daily.totalHours > 0;
                      if (lastDateWithData && daily.date > lastDateWithData) {
                        return false;
                      }
                      return hasSalary || hasHours;
                    })
                    .map((daily) => (
                      <div
                        key={daily.date}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {format(
                                new Date(daily.date),
                                'EEEE, MMMM d, yyyy',
                                locale === 'vi' ? { locale: vi } : undefined
                              )}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {format(
                                new Date(daily.date),
                                'MMM d',
                                locale === 'vi' ? { locale: vi } : undefined
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col items-end">
                            <Badge variant="secondary" className="mb-1">
                              <span data-sensitive-field="amount">
                                {formatCurrency(
                                  daily.totalSalary,
                                  data.staffForecasts[0]?.currency || 'VND'
                                )}
                              </span>
                            </Badge>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs text-muted-foreground">
                                {daily.totalHours.toFixed(1)}h
                              </span>
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-xs text-muted-foreground">
                                  {daily.staffCount}{' '}
                                  {translate('pos.salary_forecast.staff', { count: daily.staffCount })}
                                </span>
                                {daily.staffCount > 0 && (
                                  <div className="flex flex-col items-end opacity-70">
                                    {daily.fullTimeCount > 0 && (
                                      <span className="text-[10px]">
                                        {daily.fullTimeCount}{' '}
                                        {translate('pos.salary_forecast.full_time', 'Full-Time')}
                                      </span>
                                    )}
                                    {daily.partTimeCount > 0 && (
                                      <span className="text-[10px]">
                                        {daily.partTimeCount}{' '}
                                        {translate('pos.salary_forecast.shift_based', 'Shift-Based')}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModernSalaryForecastReport;

